/**
 * Season standings table, shared by the operator screen and the public
 * page. A click on a row opens that team's nights: one line per game night
 * in the season, including the nights the team skipped.
 */
import { Fragment, useState, type ReactNode } from 'react';
import { Badge, Table, Text } from '@radix-ui/themes';
import { fmtSeasonDate, ordinal } from '../../utils/seasons';
import type { SeasonNight, StandingNight, StandingRow } from './seasonsApi';

interface Props {
  rows: StandingRow[];
  nights: SeasonNight[];
  showAttendance: boolean;
  // Operator only: show the "unlinked" badge and the extra columns.
  operator?: boolean;
  // Operator only: extra content under a team's nights (links and so on).
  renderRowActions?: (row: StandingRow) => ReactNode;
}

function nightCell(n: StandingNight | undefined): string {
  if (!n) return 'Skipped';
  if (n.leftEarly) return n.leftEarlyPlace ? `Left early (${ordinal(n.leftEarlyPlace)})` : 'Left early';
  return n.placement ? ordinal(n.placement) : '—';
}

const StandingsTable: React.FC<Props> = ({ rows, nights, showAttendance, operator, renderRowActions }) => {
  const [open, setOpen] = useState<string | null>(null);
  const colCount = 5 + (showAttendance ? 1 : 0);
  const rowKey = (r: StandingRow, i: number) => r.key ?? `${r.rank}-${r.name}-${i}`;

  return (
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell align="center">#</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Team</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell align="center">Nights</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell align="center">Wins</Table.ColumnHeaderCell>
          {showAttendance && <Table.ColumnHeaderCell align="center">Bonus</Table.ColumnHeaderCell>}
          <Table.ColumnHeaderCell align="right">Points</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((r, i) => {
          const key = rowKey(r, i);
          const isOpen = open === key;
          const tied = rows.some((o) => o !== r && o.rank === r.rank);
          const byEvent = new Map(r.nights.map((n) => [n.eventId, n]));
          return (
            <Fragment key={key}>
              <Table.Row
                className="teams-row-clickable"
                onClick={() => setOpen(isOpen ? null : key)}
                aria-expanded={isOpen}
              >
                <Table.Cell align="center">
                  <Text weight="bold">{tied ? `T${r.rank}` : r.rank}</Text>
                </Table.Cell>
                <Table.RowHeaderCell>
                  <Text weight="medium">{r.name}</Text>
                  {operator && r.unlinked && (
                    <Badge ml="2" color="amber" variant="soft">unlinked</Badge>
                  )}
                </Table.RowHeaderCell>
                <Table.Cell align="center">{r.nightsAttended}</Table.Cell>
                <Table.Cell align="center">{r.wins}</Table.Cell>
                {showAttendance && <Table.Cell align="center">{r.attendancePoints}</Table.Cell>}
                <Table.Cell align="right">
                  <Text weight="bold">{r.total}</Text>
                </Table.Cell>
              </Table.Row>
              {isOpen && (
                <Table.Row>
                  <Table.Cell colSpan={colCount}>
                    <Table.Root size="1" variant="ghost">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeaderCell>Night</Table.ColumnHeaderCell>
                          {operator && <Table.ColumnHeaderCell>Name used</Table.ColumnHeaderCell>}
                          {operator && <Table.ColumnHeaderCell align="center">Score</Table.ColumnHeaderCell>}
                          <Table.ColumnHeaderCell align="center">Place</Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell align="right">Season points</Table.ColumnHeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {nights.map((night) => {
                          const n = byEvent.get(night.eventId);
                          return (
                            <Table.Row key={night.eventId}>
                              <Table.Cell>{fmtSeasonDate(night.date)}</Table.Cell>
                              {operator && <Table.Cell>{n?.nameUsed ?? '—'}</Table.Cell>}
                              {operator && (
                                <Table.Cell align="center">{n ? n.grandTotal ?? '—' : '—'}</Table.Cell>
                              )}
                              <Table.Cell align="center">
                                <Text color={n && !n.leftEarly ? undefined : 'gray'}>{nightCell(n)}</Text>
                              </Table.Cell>
                              <Table.Cell align="right">
                                {n ? (
                                  <Text color={n.counted ? undefined : 'gray'}>
                                    {n.points}
                                    {!n.counted && ' (not counted)'}
                                  </Text>
                                ) : (
                                  <Text color="gray">—</Text>
                                )}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table.Root>
                    {renderRowActions?.(r)}
                  </Table.Cell>
                </Table.Row>
              )}
            </Fragment>
          );
        })}
      </Table.Body>
    </Table.Root>
  );
};

export default StandingsTable;
